'use client'

import { useEffect, useRef, useState } from 'react'

import { CalendarDaysIcon, ArrowRightIcon, SearchIcon } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Reveal } from '@/components/ui/reveal'
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { SECTION_PADDING, SITE_CONTAINER } from '@/components/layout/container'
import { cn } from '@/lib/utils'
import { TAB_LIST, TAB_TRIGGER } from '@/components/blocks/tab-style'

export type BlogPost = {
  title: string
  description: string
  imageUrl: string
  imageAlt: string
  date: string
  category: string
  author: string
  authorLink: string
  blogLink: string
  categoryLink: string
}

type BlogProps = {
  blogPosts: BlogPost[]
}

const BlogGrid = ({ posts }: { posts: BlogPost[] }) => {
  return (
    <div className='grid gap-6 sm:grid-cols-2 lg:grid-cols-3'>
      {/* Гүйлгэх үед карт бүр ээлжлэн мандан гарна — сайтын нэгдсэн гарц */}
      {posts.map((post, index) => (
        <Reveal key={index} index={index} className='h-full'>
          <Card className='group h-full transition-all duration-300'>
            <CardHeader>
              <a href={post.blogLink} className='overflow-hidden rounded-lg'>
                <img
                  src={post.imageUrl}
                  alt={post.imageAlt}
                  className='h-59.5 w-full object-cover transition-transform duration-300 group-hover:scale-105'
                />
              </a>
            </CardHeader>
            <CardContent className='space-y-3.5'>
              <div className='flex items-center justify-between gap-1.5'>
                <div className='text-muted-foreground flex items-center gap-1.5'>
                  <CalendarDaysIcon className='size-6' />
                  <span className='text-xl'>{post.date}</span>
                </div>
                <a href={post.categoryLink}>
                  <Badge className='bg-primary/10 text-primary h-auto border-0 text-xl'>{post.category}</Badge>
                </a>
              </div>
              <h3 className='line-clamp-2 text-xl font-medium md:text-xl'>
                <a href={post.blogLink}>{post.title}</a>
              </h3>
              <p className='text-muted-foreground line-clamp-2 text-xl'>{post.description}</p>
              <div className='flex items-center justify-between'>
                <a href={post.authorLink} className='text-xl font-medium'>
                  {post.author}
                </a>
                <Button
                  size='icon'
                  variant='outline'
                  className='group-hover:border-primary group-hover:bg-primary group-hover:text-primary-foreground hover:border-primary hover:bg-primary hover:text-primary-foreground dark:group-hover:bg-primary dark:hover:bg-primary'
                  render={<a href={post.blogLink} />}
                  nativeButton={false}
                >
                  <ArrowRightIcon className='size-4 -rotate-45' />
                  <span className='sr-only'>Read more: {post.title}</span>
                </Button>
              </div>
            </CardContent>
          </Card>
        </Reveal>
      ))}
    </div>
  )
}

/** CMS-ээс ирэх толгойн хэсэг. Өгөхгүй бол блокийн анхдагч текст үлдэнэ. */
type BlogChrome = {
  eyebrow?: string
  heading?: string
  description?: string
  allLabel?: string
  searchPlaceholder?: string

  /**
   * Гүйлгэх үед дараагийн хуудсыг ачаалах server action.
   *
   * Өгөөгүй бол блок нь өгөгдсөн бичлэгүүдээ л харуулна.
   */
  loadMore?: (page: number) => Promise<{ posts: BlogPost[]; hasMore: boolean }>
}

const Blog = ({
  blogPosts,
  eyebrow,
  heading,
  description,
  allLabel = 'Бүгд',
  searchPlaceholder = 'Хайх',
  loadMore
}: BlogProps & BlogChrome) => {
  const [query, setQuery] = useState('')
  const [extra, setExtra] = useState<BlogPost[]>([])
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(Boolean(loadMore))
  const [loading, setLoading] = useState(false)
  const sentinel = useRef<HTMLDivElement>(null)

  const posts = [...blogPosts, ...extra]

  /*
   * Хуудасны төгсгөлд ойртоход дараагийн хуудсыг авчирна.
   *
   * `IntersectionObserver` нь харагдах агшинд л ажиллах тул гүйлгээгүй
   * хэрэглэгчид нэмэлт хүсэлт огт явуулахгүй.
   */
  useEffect(() => {
    const node = sentinel.current

    if (!loadMore || !hasMore || !node) return

    const observer = new IntersectionObserver(
      async entries => {
        if (!entries[0].isIntersecting || loading) return

        setLoading(true)

        const next = await loadMore(page + 1)

        setExtra(current => [...current, ...next.posts])
        setPage(current => current + 1)
        setHasMore(next.hasMore)
        setLoading(false)
      },
      { rootMargin: '400px' }
    )

    observer.observe(node)

    return () => observer.disconnect()
  }, [loadMore, hasMore, loading, page])

  // Ангиллууд нь бичлэгүүдээсээ гарна — хатуу жагсаалт байхгүй
  const categories = [allLabel, ...new Set(posts.map(post => post.category).filter(Boolean))]

  const matches = (post: BlogPost) => {
    const text = query.trim().toLowerCase()

    return text ? `${post.title} ${post.description}`.toLowerCase().includes(text) : true
  }

  return (
    <section className={SECTION_PADDING}>
      <div className={cn(SITE_CONTAINER, 'space-y-8 lg:space-y-16')}>
        {/* Header */}
        <div className='space-y-4'>
          {eyebrow && <p className='text-xl'>{eyebrow}</p>}

          <h2 className='text-2xl font-semibold md:text-3xl lg:text-4xl'>{heading ?? 'Мэдээ'}</h2>

          {description && <p className='text-muted-foreground text-xl md:text-xl'>{description}</p>}
        </div>

        {/* Tabs and Search */}
        <Tabs defaultValue={allLabel} className='gap-8 lg:gap-16'>
          <div className='flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center'>
            <ScrollArea className='max-md:bg-muted w-full rounded-lg sm:w-auto'>
              <TabsList className={TAB_LIST}>
                {categories.map(category => (
                  <TabsTrigger key={category} value={category} className={cn(TAB_TRIGGER, 'px-3 text-xl')}>
                    {category}
                  </TabsTrigger>
                ))}
              </TabsList>
              <ScrollBar orientation='horizontal' />
            </ScrollArea>

            <div className='relative max-md:w-full'>
              <div className='text-muted-foreground pointer-events-none absolute inset-y-0 left-0 flex items-center justify-center pl-3 peer-disabled:opacity-50'>
                <SearchIcon className='size-4' />
                <span className='sr-only'>Search</span>
              </div>
              <Input
                type='search'
                value={query}
                onChange={event => setQuery(event.target.value)}
                placeholder={searchPlaceholder}
                className='peer input-lg px-9 [&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none [&::-webkit-search-results-button]:appearance-none [&::-webkit-search-results-decoration]:appearance-none'
              />
            </div>
          </div>

          {/* All Posts Tab */}
          <TabsContent value={allLabel}>
            <BlogGrid posts={posts.filter(matches)} />
          </TabsContent>

          {/* Category-specific Tabs */}
          {categories.slice(1).map((category, index) => (
            <TabsContent key={index} value={category}>
              <BlogGrid posts={posts.filter(post => post.category === category).filter(matches)} />
            </TabsContent>
          ))}
        </Tabs>

        {/* Гүйлгэлтийн мэдрэгч — доод ирмэгт ойртоход дараагийн хуудас ачаална */}
        {loadMore && hasMore && (
          <div ref={sentinel} className='flex justify-center py-6'>
            <span className='text-muted-foreground text-xl'>{loading ? '…' : ''}</span>
          </div>
        )}
      </div>
    </section>
  )
}

export default Blog
